// Cave84 — Notifications hooks (Phase 7 Week 19)

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { Notification } from '@cave84/shared-types';
import {
  createNotification,
  listNotifications,
  markAllRead,
  markRead,
} from '@/services/notifications';
import { useAuthStore } from '@/stores/auth.store';

const QK_NOTIFS = (uid: string) => ['notifications', uid] as const;

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useNotifications(): UseQueryResult<Notification[]> {
  const uid = useAuthStore(s => s.userDoc?.id ?? '');
  return useQuery({
    queryKey: QK_NOTIFS(uid),
    queryFn: () => (uid ? listNotifications(uid) : Promise.resolve([])),
    enabled: !!uid,
    staleTime: 30_000, // 30 sec — notifications update infrequently
    refetchInterval: 60_000, // poll every 60 sec
  });
}

export function useUnreadCount(): number {
  const { data: notifications = [] } = useNotifications();
  return notifications.filter(n => !n.read).length;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useMarkRead(): UseMutationResult<void, Error, string> {
  const uid = useAuthStore(s => s.userDoc?.id ?? '');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_NOTIFS(uid) });
    },
  });
}

export function useMarkAllRead(): UseMutationResult<void, Error, void> {
  const uid = useAuthStore(s => s.userDoc?.id ?? '');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllRead(uid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_NOTIFS(uid) });
    },
  });
}

export function useCreateNotification(): UseMutationResult<
  string,
  Error,
  Parameters<typeof createNotification>
> {
  const uid = useAuthStore(s => s.userDoc?.id ?? '');
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => createNotification(...args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_NOTIFS(uid) });
    },
  });
}
